import * as Sentry from '@sentry/react';
import type { z } from 'zod';

/**
 * Parse an API response against its schema, reporting any mismatch as a distinct, monitored
 * "contract drift" signal before rethrowing. A backend response that diverges from the agreed
 * wire contract otherwise surfaces only as a generic empty/errored list — this makes it a loud,
 * greppable Sentry error keyed by endpoint, caught on the first bad response rather than in QA.
 *
 * Behaviour is otherwise identical to `schema.parse(...)`: it returns the parsed value on success
 * and throws the `ZodError` on failure, so callers/UI states are unchanged.
 */
export function parseContract<T>(schema: z.ZodType<T>, data: unknown, endpoint: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    Sentry.captureException(result.error, {
      tags: { contract_drift: endpoint },
      extra: { issues: result.error.issues },
    });
    throw result.error;
  }
  return result.data;
}
